import axios from "axios";
import { get } from "@/services/axiosUitils";
import {
  ScreenerRequest,
  ScreenerResponse,
  ScreenerResponseById,
  CompanyBoardOfDirectors,
  CompanyHistory,
  NSEData,
  BSEData,
  ProfitAndLoss,
  CompanyApiResponse,
  ResultsApiResponse,
  ShareholdingPatternResponse,
  ShareholdingMoreThan1PercentResponse,
  SubsidiaryJVCollaborationResponse,
  BankersResponse,
  BonusEventsResponse,
  CompanyRightsIssueEventsResponse,
  CompanyAGMEventsResponse,
  CompanyDividendEventsResponse,
  AnnualReportResponse,
  PivotFibonacciResponse,
  CompanyChangeOfNameEventsResponse,
} from "@/utils/types/Screener/Screener";

const basePath = "/screener/company-master/";
const boardOfDirectorsBasePath = "/screener/board-of-directors";
const historyBasePath = "/screener/company-history";
const nseBasePath = "/screener/delayed-price/nse";
const bseBasePath = "/screener/delayed-price/bse";
const profitAndLossBasePath = "/screener/profit-and-loss";
const ttmDataBasePath = "/screener/ttm-data/by-co-code";
const quarterlyResultsDataBasePath = "/screener/quarterly-results/by-co-code";
const plResultsDataBasePath = "/screener/profit-loss/by-co-code";
const cashFlowBasePath = "/screener/cash-flow/by-co-code";
const balanceSheetBasePath = "/screener/balance-sheet/by-co-code";
const halfYearlyResultsDataBasePath =
  "/screener/half-yearly-results/by-co-code";
const shareholdingPatternBasePath =
  "/screener/share-holding-aggregate/by-co-code";
const shareholdingMoreThan1PercentBasePath =
  "/screener/share-holding-pattern/by-co-code";
const subsidiaryJVCollaborationBasePath =
  "/screener/subsidiaries-jvs-collaborations";
const bankersBasePath = "/screener/bankers";
const bonusEventsBasePath = "/screener/company-bonus/";
const companyRightsIssueEventsBasePath = "/screener/company-rights/";
const companyAGMEventsBasePath = "/screener/company-agm/";
const companySplitEventsBasePath = "/screener/company-split/";
const companyDividendEventsBasePath = "/screener/company-dividend/";
const annualReportBasePath = "/screener/annual-report/";
const pivotFibonacciBasePath = "/screener/pivot-fibonacci/";
const companyChangeOfNameEventsBasePath = "/screener/company-change-of-name/";

class ScreenerService {
  getCompanyMaster = async (
    request: ScreenerRequest
  ): Promise<ScreenerResponse> => {
    return await get<ScreenerResponse>(basePath, {
      params: request,
    });
  };

  getCompanyMasterById = async (id: string): Promise<ScreenerResponseById> => {
    return await get<ScreenerResponseById>(`${basePath}${id}/`);
  };

  getCompanyHistory = async (id: string): Promise<CompanyHistory> => {
    return await get<CompanyHistory>(`${historyBasePath}/?co_code=${id}`);
  };

  getCompanyBoardOfDirectors = async (
    id: string
  ): Promise<CompanyBoardOfDirectors> => {
    return await get<CompanyBoardOfDirectors>(
      `${boardOfDirectorsBasePath}/?co_code=${id}`
    );
  };

  getNSEData = async (id: string): Promise<NSEData> => {
    return await get<NSEData>(`${nseBasePath}/?co_code=${id}&exchange=NSE`);
  };

  getBSEData = async (id: string): Promise<BSEData> => {
    return await get<BSEData>(`${bseBasePath}/?co_code=${id}&exchange=BSE`);
  };

  getNSEAndBSEData = async (
    id: string,
    exchange: "nse" | "bse"
  ): Promise<NSEData> => {
    // Use relative path for development (Vite proxy) and absolute for production
    const isDev = import.meta.env.MODE === "development";
    const baseURL = isDev
      ? "/price-sync-api"
      : import.meta.env.VITE_PRICE_SYNC_API_BASE_URL;

    const response = await axios.get<NSEData>(
      `${baseURL}/delayed-price/?co_code=${id}&exchange=${exchange}`
    );
    return response.data;
  };

  getProfitAndLoss = async (id: string): Promise<ProfitAndLoss> => {
    return await get<ProfitAndLoss>(`${profitAndLossBasePath}/?co_code=${id}`);
  };

  getTTMData = async (id: string): Promise<CompanyApiResponse> => {
    return await get<CompanyApiResponse>(
      `${ttmDataBasePath}/${id}/?result_type=standalone`
    );
  };
  getQuarterlyResultsData = async (
    id: string,
    resultType: "standalone" | "consolidated",
    fromYear: string,
    toYear: string
  ): Promise<ResultsApiResponse> => {
    return await get<ResultsApiResponse>(
      `${quarterlyResultsDataBasePath}/${id}/?result_type=${resultType}&from_year=${fromYear}&to_year=${toYear}`
    );
  };

  getProfitAndLossResultsData = async (
    id: string,
    resultType: "standalone" | "consolidated",
    fromYear: string,
    toYear: string
  ): Promise<ResultsApiResponse> => {
    return await get<ResultsApiResponse>(
      `${plResultsDataBasePath}/${id}/?result_type=${resultType}&from_year=${fromYear}&to_year=${toYear}`
    );
  };

  getCashFlowResultsData = async (
    id: string,
    resultType: "standalone" | "consolidated",
    fromYear: string,
    toYear: string
  ): Promise<ResultsApiResponse> => {
    return await get<ResultsApiResponse>(
      `${cashFlowBasePath}/${id}/?result_type=${resultType}&from_year=${fromYear}&to_year=${toYear}`
    );
  };

  getBalanceSheetResultsData = async (
    id: string,
    resultType: "standalone" | "consolidated",
    fromYear: string,
    toYear: string
  ): Promise<ResultsApiResponse> => {
    return await get<ResultsApiResponse>(
      `${balanceSheetBasePath}/${id}/?result_type=${resultType}&from_year=${fromYear}&to_year=${toYear}`
    );
  };
  getHalfYearlyResultsData = async (
    id: string,
    resultType: "standalone" | "consolidated",
    fromYear: string,
    toYear: string
  ): Promise<ResultsApiResponse> => {
    return await get<ResultsApiResponse>(
      `${halfYearlyResultsDataBasePath}/${id}/?result_type=${resultType}&from_year=${fromYear}&to_year=${toYear}`
    );
  };

  getShareholdingPatternData = async (
    id: string,
    fromYear: string,
    toYear: string
  ): Promise<ShareholdingPatternResponse> => {
    return await get<ShareholdingPatternResponse>(
      `${shareholdingPatternBasePath}/${id}/?from_year=${fromYear}&to_year=${toYear}`
    );
  };

  getShareholdingMoreThan1PercentData = async (
    id: string
  ): Promise<ShareholdingMoreThan1PercentResponse> => {
    return await get<ShareholdingMoreThan1PercentResponse>(
      `${shareholdingMoreThan1PercentBasePath}/${id}/`
    );
  };

  getSubsidiaryJVCollaborationData = async (
    id: string
  ): Promise<SubsidiaryJVCollaborationResponse> => {
    return await get<SubsidiaryJVCollaborationResponse>(
      `${subsidiaryJVCollaborationBasePath}/?co_code=${id}`
    );
  };

  getBankersData = async (id: string): Promise<BankersResponse> => {
    return await get<BankersResponse>(`${bankersBasePath}/?co_code=${id}`);
  };

  getBonusEventsData = async (id: string): Promise<BonusEventsResponse> => {
    return await get<BonusEventsResponse>(
      `${bonusEventsBasePath}by-co-code/${id}/`
    );
  };

  getCompanyRightsIssueEventsData = async (
    id: string
  ): Promise<CompanyRightsIssueEventsResponse> => {
    return await get<CompanyRightsIssueEventsResponse>(
      `${companyRightsIssueEventsBasePath}by-co-code/${id}/`
    );
  };

  getCompanyAGMEventsData = async (
    id: string
  ): Promise<CompanyAGMEventsResponse> => {
    return await get<CompanyAGMEventsResponse>(
      `${companyAGMEventsBasePath}by-co-code/${id}/`
    );
  };

  getCompanySplitEventsData = async (
    id: string
  ): Promise<BonusEventsResponse> => {
    return await get<BonusEventsResponse>(
      `${companySplitEventsBasePath}by-co-code/${id}/`
    );
  };

  getCompanyDividendEventsData = async (
    id: string
  ): Promise<CompanyDividendEventsResponse> => {
    return await get<CompanyDividendEventsResponse>(
      `${companyDividendEventsBasePath}by-co-code/${id}/`
    );
  };

  getAnnualReportData = async (id: string): Promise<AnnualReportResponse> => {
    return await get<AnnualReportResponse>(
      `${annualReportBasePath}by-co-code/${id}/`
    );
  };

  getPivotFibonacciData = async (
    id: string,
    timeFrame: string,
    exchange: string
  ): Promise<PivotFibonacciResponse> => {
    return await get<PivotFibonacciResponse>(
      `${pivotFibonacciBasePath}by-co-code/${id}/?time_frame=${timeFrame}&exchange=${exchange}`
    );
  };

  getCompanyChangeOfNameEventsData = async (
    id: string
  ): Promise<CompanyChangeOfNameEventsResponse> => {
    return await get<CompanyChangeOfNameEventsResponse>(
      `${companyChangeOfNameEventsBasePath}by-co-code/${id}/`
    );
  };
}

export default new ScreenerService();
